import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';
import { blockedUsersHydratedAtom, blockedUsersListParamsAtom } from '@/store';

export interface BlockedUsersSearchFields {
  search: string;
  domain_id: string;
}

export function useBlockedUsersFilters() {
  const [params, setParams] = useAtom(blockedUsersListParamsAtom);
  const [, setHydrated] = useAtom(blockedUsersHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    search: parseAsString.withDefault(''),
    domain_id: parseAsString.withDefault('')
  });

  const [searchFields, setSearchFields] = React.useState<BlockedUsersSearchFields>({
    search: queryParams.search,
    domain_id: queryParams.domain_id
  });

  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  React.useEffect(() => {
    const filters: Record<string, string | number | undefined> = {};
    if (queryParams.search) filters.search = queryParams.search;
    const domainIdParsed = queryParams.domain_id ? parseInt(queryParams.domain_id, 10) : undefined;
    if (domainIdParsed !== undefined && !Number.isNaN(domainIdParsed)) {
      filters.domain_id = domainIdParsed;
    }

    setParams((prev) => {
      const newParams = {
        ...prev,
        page: queryParams.page,
        filters
      };
      if (
        prev.page === newParams.page &&
        JSON.stringify(prev.filters) === JSON.stringify(newParams.filters)
      ) {
        return prev;
      }
      return newParams;
    });

    setHydrated(true);
  }, [queryParams, setParams, setHydrated]);

  React.useEffect(() => {
    setSearchFields({
      search: queryParams.search,
      domain_id: queryParams.domain_id
    });
  }, []);

  const applyFilters = React.useCallback(async () => {
    const domainIdParsed = searchFields.domain_id ? parseInt(searchFields.domain_id, 10) : undefined;
    const nextFilters: Record<string, string | number | undefined> = {};
    if (searchFields.search) nextFilters.search = searchFields.search;
    if (domainIdParsed !== undefined && !Number.isNaN(domainIdParsed)) {
      nextFilters.domain_id = domainIdParsed;
    }

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));

    await setQueryParams({
      page: 1,
      search: searchFields.search,
      domain_id: searchFields.domain_id
    });

    setIsSearchOpen(false);
  }, [searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setSearchFields({ search: '', domain_id: '' });
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      search: '',
      domain_id: ''
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(
    () => Boolean(searchFields.search || searchFields.domain_id),
    [searchFields]
  );

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  return {
    searchFields,
    setSearchFields,
    isSearchOpen,
    setIsSearchOpen,
    hasAnyFilter,
    applyFilters,
    clearFilters,
    handlePageChange
  };
}
